'use client';

import { useEffect } from 'react';
import config from 'config';

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleCartBasket } from 'store/reducers/productsSlice';

import { formatPhoneNumbers } from 'utils';

import { Carousel, Divider, Link } from 'ui';

const {
  shop: {
    cart: {
      modalBanners,
      errorModal: { title, description, phone: defaultPhone }
    }
  }
} = config;

interface ContactUsErrorModalProps {
  errorText: string;
  onClearHandler: () => void;
}

const ContactUsErrorModal = ({
  errorText = '',
  onClearHandler
}: ContactUsErrorModalProps) => {
  const {
    colors: {
      palette: {
        dashboard: {
          memberships: { modal: modalStyles }
        }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { isCartBasketOpen } = useAppSelector((state) => state.products);
  const { selectedLocation } = useAppSelector((state) => state.user);

  const phone = selectedLocation?.phone || defaultPhone;
  const formattedPhone = formatPhoneNumbers(phone);
  const isModalOpen = Boolean(errorText);

  useEffect(() => {
    if (isCartBasketOpen && isModalOpen) {
      dispatch(toggleCartBasket());
    }
  }, [isModalOpen, isCartBasketOpen, dispatch]);

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClearHandler}
      size={{ base: '3xl', md: '6xl' }}
      isCentered
    >
      <ModalOverlay bg={modalStyles.overlayBackground} opacity='0.1' />
      <ModalContent borderRadius='12px' overflow='hidden' margin='15px'>
        <ModalBody p={0}>
          <Flex gap={0} justifyContent='space-between'>
            <VStack
              alignItems='center'
              justifyContent='center'
              w='100%'
              gap={{ base: '30px', sm: '35px' }}
              py={{ base: '40px', md: '62px' }}
              px={{ base: '30px', sm: '50px', md: '60px', lg: '80px' }}
            >
              <Box textAlign='center'>
                <Text
                  fontSize={{ base: '16px', lg: '18px' }}
                  lineHeight={{ base: '20px', lg: '22px' }}
                  color={modalStyles.titleColor}
                  textTransform='uppercase'
                >
                  {errorText ?? title}
                </Text>
              </Box>

              <Divider w='100%' borderBottomColor={modalStyles.dividerColor} />
              <VStack justifyContent='center' gap={{ base: 1, md: 2, lg: 4 }}>
                <Text
                  fontSize={{ base: '20px', md: '24px', lg: '28px' }}
                  fontWeight='normal'
                  lineHeight='160%'
                  color={modalStyles.descriptionColor}
                  px={{ base: 4, lg: '70px', xl: '120px' }}
                  textAlign='center'
                >
                  {description}
                </Text>
                <Link
                  to={`tel:${formattedPhone}`}
                  sx={{
                    fontSize: { base: '20px', md: '24px', lg: '28px' },
                    color: modalStyles.descriptionColor,
                    fontFamily: 'main',
                    fontWeight: 'bold',
                    _hover: { textDecoration: 'underline' }
                  }}
                >
                  {formattedPhone}
                </Link>
              </VStack>
              <Divider w='100%' borderBottomColor={modalStyles.dividerColor} />

              <Stack gap='20px'>
                <Flex
                  w='100%'
                  justifyContent='center'
                  mt='auto'
                  gap='31px'
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <Button
                    as={Link}
                    to={`tel:${formattedPhone}`}
                    w='215px'
                    variant='solid'
                    height='45px'
                    colorScheme='black'
                    fontSize='14px'
                  >
                    Call now
                  </Button>
                </Flex>

                <Button
                  variant='link'
                  onClick={onClearHandler}
                  color={modalStyles.backBtnColor}
                  textTransform='uppercase'
                  fontSize='14px'
                  _hover={{
                    textDecoration: 'none'
                  }}
                >
                  Back
                </Button>
              </Stack>
            </VStack>
            <Box
              display={{ base: 'none', lg: 'block' }}
              maxW='345px'
              overflow='hidden'
              width='100%'
              marginRight='-3px'
              sx={{
                '& .slick-dots': {
                  bottom: '30px',
                  '& li': {
                    button: {
                      width: '10px',
                      height: '10px',
                      border: '1px solid',
                      borderColor: 'transparent',
                      borderRadius: '50%',
                      background: 'transparent',
                      _before: {
                        display: 'none'
                      }
                    }
                  }
                }
              }}
            >
              <Carousel
                banners={modalBanners}
                settings={{
                  dots: true
                }}
              />
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ContactUsErrorModal;
